import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { Search, User, CheckCircle2, XCircle, Eye, ExternalLink, Linkedin, FileText } from 'lucide-react';

const AdminTalent = () => {
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [functionFilter, setFunctionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchTalents();
  }, [filter, functionFilter]);

  const fetchTalents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (functionFilter !== 'all') params.append('function', functionFilter);
      
      const response = await axios.get(`${API}/admin/talent?${params.toString()}`);
      setTalents(response.data);
    } catch (error) {
      console.error('Error fetching talents:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTalentStatus = async (talentId, newStatus, notes = null) => {
    setUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append('status', newStatus);
      if (notes !== null) params.append('admin_notes', notes);
      
      await axios.put(`${API}/admin/talent/${talentId}?${params.toString()}`);
      toast.success(`Talent ${newStatus}`);
      fetchTalents();
      setSelectedTalent(null);
    } catch (error) {
      toast.error('Failed to update talent');
    } finally {
      setUpdating(false);
    }
  };

  const filteredTalents = talents.filter(t => {
    if (search) {
      const searchLower = search.toLowerCase();
      return t.name?.toLowerCase().includes(searchLower) || 
             t.headline?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const statusColors = {
    pending: 'badge-warning',
    approved: 'badge-success',
    rejected: 'badge-error',
  };

  const functionColors = {
    product: 'badge-primary',
    design: 'badge-accent',
    engineering: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <AdminLayout>
      <div data-testid="admin-talent-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">Manage Talent</h1>
            <p className="text-slate-600">{filteredTalents.length} talent profiles</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6" data-testid="talent-filters">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Select value={functionFilter} onValueChange={setFunctionFilter}>
              <SelectTrigger className="w-40" data-testid="function-filter">
                <SelectValue placeholder="Function" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Talent Table */}
        <Card className="overflow-hidden" data-testid="talent-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Name</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Function</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Experience</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Availability</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
                      </td>
                    </tr>
                  ))
                ) : filteredTalents.length > 0 ? (
                  filteredTalents.map((talent) => (
                    <tr key={talent.id} className="hover:bg-slate-50" data-testid={`talent-row-${talent.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{talent.name}</p>
                            <p className="text-sm text-slate-500 truncate max-w-[200px]">{talent.headline}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${functionColors[talent.function]} px-2 py-0.5 rounded-full text-xs`}>
                          {talent.function}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{talent.experience_years} years</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{talent.availability} hrs/week</td>
                      <td className="px-6 py-4">
                        <span className={`${statusColors[talent.status]} text-xs`}>{talent.status}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedTalent(talent);
                              setAdminNotes(talent.admin_notes || '');
                            }}
                            data-testid={`view-talent-${talent.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {talent.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => updateTalentStatus(talent.id, 'approved')}
                                data-testid={`approve-talent-${talent.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => updateTalentStatus(talent.id, 'rejected')}
                                data-testid={`reject-talent-${talent.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No talent found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Talent Detail Modal */}
      <Dialog open={!!selectedTalent} onOpenChange={() => setSelectedTalent(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="talent-detail-modal">
          <DialogHeader>
            <DialogTitle>Talent Profile</DialogTitle>
          </DialogHeader>
          
          {selectedTalent && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedTalent.name}</h3>
                  <p className="text-sm text-slate-500">{selectedTalent.headline}</p>
                  <span className={`${statusColors[selectedTalent.status]} text-xs mt-1 inline-block`}>
                    {selectedTalent.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Function</span>
                  <p className="font-medium">{selectedTalent.function}</p>
                </div>
                <div>
                  <span className="text-slate-500">Experience</span>
                  <p className="font-medium">{selectedTalent.experience_years} years</p>
                </div>
                <div>
                  <span className="text-slate-500">Availability</span>
                  <p className="font-medium">{selectedTalent.availability} hrs/week</p>
                </div>
                <div>
                  <span className="text-slate-500">Timezone</span>
                  <p className="font-medium">{selectedTalent.timezone || 'Not set'}</p>
                </div>
              </div>

              {selectedTalent.bio && (
                <div>
                  <span className="text-sm text-slate-500">Bio</span>
                  <p className="text-sm text-slate-700 mt-1">{selectedTalent.bio}</p>
                </div>
              )}

              <div className="flex gap-4">
                {selectedTalent.linkedin_url && (
                  <a href={selectedTalent.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Linkedin className="h-4 w-4 mr-2" />
                      LinkedIn
                    </Button>
                  </a>
                )}
                {selectedTalent.resume_url && (
                  <a href={selectedTalent.resume_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Resume
                    </Button>
                  </a>
                )}
              </div>

              <div>
                <span className="text-sm text-slate-500">Admin Notes</span>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this talent..."
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                {selectedTalent.status !== 'approved' && (
                  <Button
                    onClick={() => updateTalentStatus(selectedTalent.id, 'approved', adminNotes)}
                    disabled={updating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                )}
                {selectedTalent.status !== 'rejected' && (
                  <Button
                    variant="destructive"
                    onClick={() => updateTalentStatus(selectedTalent.id, 'rejected', adminNotes)}
                    disabled={updating}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTalent;
